/**
 * Comparison data for the Klapzo-vs-X marketing pages (THE-6014).
 *
 * Comparative advertising in Brazil must be truthful, objective and verifiable.
 * Rule of this file: a competitor fact exists only together with its public
 * source URL and the date it was read (`Sourced`). Anything we could not verify
 * is `null` / `"unverified"` and the page renders "não verificado". We make no
 * claim about Klapzo's own price or plans (billing is dark; sdd/pricing.md is a
 * recommendation, not a decision).
 */
import type { Lang } from "./Landing.js";

export type Verdict = "yes" | "partial" | "no";

/** Public source of a competitor fact: where it was read and when (ISO date). */
export interface SourceRef {
  url: string;
  /** ISO date (YYYY-MM-DD) the page was read. */
  readAt: string;
}

/** A localized competitor fact. Never exists without its source. */
export interface Sourced {
  text: Record<Lang, string>;
  source: SourceRef;
}

/**
 * A row in the feature matrix. The competitor side is either a verdict WITH a
 * source, or unverified (`them: null`, `source: null`). Klapzo's own verdict is
 * about the product we ship.
 */
export type FeatureRow = {
  key: string;
  label: Record<Lang, string>;
  klapzo: Verdict;
} & (
  | { them: Verdict; source: SourceRef }
  | { them: null; source: null }
);

export interface Competitor {
  slug: string;
  name: string;
  /** Short category, localized (e.g. "Quiz gamificado"). */
  category: Record<Lang, string>;
  /** Neutral positioning line; no numbers, prices or feature claims. */
  blurb: Record<Lang, string>;
  /** Free-tier fact; `null` = not verified. */
  freeTier: Sourced | null;
  /** Pricing fact; `null` = not verified. */
  pricing: Sourced | null;
  /** Fair "choose them if…", free of unverified facts. */
  whenItFits: Record<Lang, string>;
  rows: FeatureRow[];
}

type L = Record<Lang, string>;

/** Shared feature dimensions. Competitor verdicts are unverified until a source is attached. */
function row(key: string, label: L, klapzo: Verdict = "yes"): FeatureRow {
  return { key, label, klapzo, them: null, source: null };
}

const LABELS = {
  live: { "pt-BR": "Interação ao vivo (enquetes em tempo real)", "en-US": "Live interaction (real-time polls)", "es-LATAM": "Interacción en vivo (encuestas en tiempo real)" },
  quiz: { "pt-BR": "Quiz pontuado com ranking", "en-US": "Scored quiz with leaderboard", "es-LATAM": "Quiz puntuado con ranking" },
  wordcloud: { "pt-BR": "Nuvem de palavras", "en-US": "Word cloud", "es-LATAM": "Nube de palabras" },
  qa: { "pt-BR": "Perguntas abertas com curtidas (Q&A)", "en-US": "Open Q&A with upvotes", "es-LATAM": "Preguntas abiertas con votos (Q&A)" },
  library: { "pt-BR": "Biblioteca de métodos de facilitação", "en-US": "Facilitation method library", "es-LATAM": "Biblioteca de métodos de facilitación" },
  planner: { "pt-BR": "Planejador de agenda (arrastar e soltar)", "en-US": "Drag-and-drop agenda planner", "es-LATAM": "Planificador de agenda (arrastrar y soltar)" },
  allinone: { "pt-BR": "Engajamento + planejamento no mesmo lugar", "en-US": "Engagement + planning in one place", "es-LATAM": "Interacción + planificación en un solo lugar" },
  ptbr: { "pt-BR": "Português nativo (pt/en/es)", "en-US": "Native Portuguese (pt/en/es)", "es-LATAM": "Portugués nativo (pt/en/es)" },
  lgpd: { "pt-BR": "Anônimo por padrão · LGPD", "en-US": "Anonymous by default · LGPD", "es-LATAM": "Anónimo por defecto · LGPD" },
  ai: { "pt-BR": "IA para rascunhos (com revisão humana)", "en-US": "AI drafts (human-reviewed)", "es-LATAM": "IA para borradores (con revisión humana)" },
  noinstall: { "pt-BR": "Sem instalar app (código/QR)", "en-US": "No app install (code/QR)", "es-LATAM": "Sin instalar app (código/QR)" },
} satisfies Record<string, L>;

const COMPETITORS: Competitor[] = [
  {
    slug: "kahoot",
    name: "Kahoot!",
    category: { "pt-BR": "Quiz gamificado", "en-US": "Game-based quiz", "es-LATAM": "Quiz gamificado" },
    blurb: { "pt-BR": "Plataforma de quiz gamificado.", "en-US": "A game-based quiz platform.", "es-LATAM": "Plataforma de quiz gamificado." },
    freeTier: null,
    pricing: null,
    whenItFits: { "pt-BR": "Escolha o Kahoot! se o seu foco é quiz gamificado e você já usa a ferramenta. Confira limites e planos no site oficial.", "en-US": "Choose Kahoot! if your focus is game-based quizzes and you already use the tool. Check limits and plans on the official site.", "es-LATAM": "Elige Kahoot! si tu foco es el quiz gamificado y ya usas la herramienta. Consulta límites y planes en el sitio oficial." },
    rows: [
      row("live", LABELS.live),
      row("quiz", LABELS.quiz),
      row("wordcloud", LABELS.wordcloud),
      row("qa", LABELS.qa),
      row("library", LABELS.library),
      row("planner", LABELS.planner),
      row("allinone", LABELS.allinone),
      row("ptbr", LABELS.ptbr),
      row("lgpd", LABELS.lgpd),
      row("ai", LABELS.ai),
      row("noinstall", LABELS.noinstall),
    ],
  },
  {
    slug: "mentimeter",
    name: "Mentimeter",
    category: { "pt-BR": "Apresentações interativas", "en-US": "Interactive presentations", "es-LATAM": "Presentaciones interactivas" },
    blurb: { "pt-BR": "Ferramenta de apresentações interativas.", "en-US": "An interactive presentation tool.", "es-LATAM": "Herramienta de presentaciones interactivas." },
    freeTier: {
      text: { "pt-BR": "50 participantes por mês", "en-US": "50 participants per month", "es-LATAM": "50 participantes por mes" },
      source: { url: "https://www.mentimeter.com/plans", readAt: "2026-10-12" },
    },
    pricing: {
      text: { "pt-BR": "Basic US$ 11,99 e Pro US$ 24,99 por apresentador/mês, no plano anual", "en-US": "Basic US$11.99 and Pro US$24.99 per presenter/month, billed annually", "es-LATAM": "Basic US$ 11,99 y Pro US$ 24,99 por presentador/mes, con facturación anual" },
      source: { url: "https://www.mentimeter.com/plans", readAt: "2026-10-12" },
    },
    whenItFits: { "pt-BR": "Escolha o Mentimeter se você quer uma ferramenta de apresentações interativas independente e o plano grátis cobre o seu uso. Confira os limites no site oficial.", "en-US": "Choose Mentimeter if you want a standalone interactive presentation tool and its free plan covers your use. Check the limits on the official site.", "es-LATAM": "Elige Mentimeter si quieres una herramienta de presentaciones interactivas independiente y su plan gratis cubre tu uso. Consulta los límites en el sitio oficial." },
    rows: [
      row("live", LABELS.live),
      row("quiz", LABELS.quiz),
      row("wordcloud", LABELS.wordcloud),
      row("qa", LABELS.qa),
      row("library", LABELS.library),
      row("planner", LABELS.planner),
      row("allinone", LABELS.allinone),
      row("ptbr", LABELS.ptbr),
      row("lgpd", LABELS.lgpd),
      row("ai", LABELS.ai),
      row("noinstall", LABELS.noinstall),
    ],
  },
  {
    slug: "slido",
    name: "Slido",
    category: { "pt-BR": "Q&A e enquetes para reuniões", "en-US": "Q&A & polls for meetings", "es-LATAM": "Q&A y encuestas para reuniones" },
    blurb: { "pt-BR": "Ferramenta de Q&A e enquetes para reuniões.", "en-US": "A Q&A and polling tool for meetings.", "es-LATAM": "Herramienta de Q&A y encuestas para reuniones." },
    freeTier: null,
    pricing: null,
    whenItFits: { "pt-BR": "Escolha o Slido se o seu foco é Q&A e enquetes em reuniões e você já usa a ferramenta. Confira limites e planos no site oficial.", "en-US": "Choose Slido if your focus is Q&A and polls in meetings and you already use the tool. Check limits and plans on the official site.", "es-LATAM": "Elige Slido si tu foco es Q&A y encuestas en reuniones y ya usas la herramienta. Consulta límites y planes en el sitio oficial." },
    rows: [
      row("live", LABELS.live),
      row("quiz", LABELS.quiz),
      row("wordcloud", LABELS.wordcloud),
      row("qa", LABELS.qa),
      row("library", LABELS.library),
      row("planner", LABELS.planner),
      row("allinone", LABELS.allinone),
      row("ptbr", LABELS.ptbr),
      row("lgpd", LABELS.lgpd),
      row("ai", LABELS.ai),
      row("noinstall", LABELS.noinstall),
    ],
  },
  {
    slug: "sessionlab",
    name: "SessionLab",
    category: { "pt-BR": "Planejamento de workshops", "en-US": "Workshop planning", "es-LATAM": "Planificación de talleres" },
    blurb: { "pt-BR": "Ferramenta de planejamento de workshops.", "en-US": "A workshop planning tool.", "es-LATAM": "Herramienta de planificación de talleres." },
    freeTier: null,
    pricing: {
      text: { "pt-BR": "Individual US$ 9, Pro US$ 15 e Business US$ 23 por mês, no plano anual", "en-US": "Individual US$9, Pro US$15 and Business US$23 per month, billed annually", "es-LATAM": "Individual US$ 9, Pro US$ 15 y Business US$ 23 por mes, con facturación anual" },
      source: { url: "https://www.sessionlab.com/pricing", readAt: "2026-10-12" },
    },
    whenItFits: { "pt-BR": "Escolha o SessionLab se o seu foco é só planejar workshops e você já usa a ferramenta. Confira os planos no site oficial.", "en-US": "Choose SessionLab if your focus is only planning workshops and you already use the tool. Check the plans on the official site.", "es-LATAM": "Elige SessionLab si tu foco es solo planificar talleres y ya usas la herramienta. Consulta los planes en el sitio oficial." },
    rows: [
      row("live", LABELS.live),
      row("quiz", LABELS.quiz),
      row("wordcloud", LABELS.wordcloud),
      row("qa", LABELS.qa),
      row("library", LABELS.library),
      row("planner", LABELS.planner),
      row("allinone", LABELS.allinone),
      row("ptbr", LABELS.ptbr),
      row("lgpd", LABELS.lgpd),
      row("ai", LABELS.ai),
      row("noinstall", LABELS.noinstall),
    ],
  },
];

export function allCompetitors(): Competitor[] {
  return COMPETITORS;
}
export function getCompetitor(slug: string): Competitor | undefined {
  return COMPETITORS.find((c) => c.slug === slug);
}
